/**
 * A fixed-capacity circular buffer of timestamped samples, generic over the
 * sample type.
 *
 * There is one of these behind every signal in the project — MouthSample at
 * ~30 Hz, EnvelopeSample at ~375 Hz, AudioFrame at rAF rate — and exactly one
 * method matters:
 *
 *     sampleAt(t)   what was this signal doing at time t?
 *
 * That question is the whole reason the buffers exist. A character of transcript
 * is bound to the moment it was spoken, not the moment ASR got around to
 * reporting it, so the renderer asks the mouth buffer what the face was doing
 * ~300 ms ago. The buffers are also the boundary rule 3 of the architecture
 * section draws: analysers write here, the renderer reads here on rAF, and
 * feature extraction never touches the DOM.
 *
 * Samples must be pushed in non-decreasing `t` order. Every producer in this
 * project timestamps with `performance.now()` at capture, so that holds for
 * free — but the binary search assumes it and does not verify it.
 */

/**
 * Blend two samples field by field.
 *
 * Numeric fields interpolate linearly. Anything else (a string `type`, a
 * boolean flag) takes the nearer neighbour's value, because there is no
 * meaningful midpoint between 'bilabial' and 'alveolar'.
 *
 * @template T
 * @param {T} a
 * @param {T} b
 * @param {number} alpha 0 at `a`, 1 at `b`
 * @param {number} t timestamp to stamp on the result
 * @returns {T}
 */
function blend(a, b, alpha, t) {
  /** @type {Record<string, unknown>} */
  const out = {};
  for (const key of Object.keys(a)) {
    const av = a[key];
    const bv = b[key];
    out[key] =
      typeof av === 'number' && typeof bv === 'number'
        ? av + (bv - av) * alpha
        : alpha < 0.5
          ? av
          : bv;
  }
  out.t = t;
  return /** @type {T} */ (out);
}

/**
 * @template {{ t: number }} T
 */
export class RingBuffer {
  /** @param {number} capacity samples retained before the oldest is dropped */
  constructor(capacity) {
    if (!Number.isInteger(capacity) || capacity < 1) {
      throw new RangeError(`RingBuffer capacity must be a positive integer, got ${capacity}`);
    }
    this.capacity = capacity;
    /** @type {(T|undefined)[]} */
    this._buf = new Array(capacity);
    /** Physical index of the oldest sample. @type {number} */
    this._start = 0;
    /** @type {number} */
    this._len = 0;
  }

  /** Number of samples currently held. @returns {number} */
  get length() {
    return this._len;
  }

  /**
   * Sample at logical index `i`, 0 being the oldest.
   * @param {number} i
   * @returns {T}
   */
  at(i) {
    return /** @type {T} */ (this._buf[(this._start + i) % this.capacity]);
  }

  /** @returns {T|null} */
  oldest() {
    return this._len ? this.at(0) : null;
  }

  /** @returns {T|null} */
  latest() {
    return this._len ? this.at(this._len - 1) : null;
  }

  /**
   * Append a sample, overwriting the oldest once at capacity.
   * @param {T} sample
   */
  push(sample) {
    if (this._len < this.capacity) {
      this._buf[(this._start + this._len) % this.capacity] = sample;
      this._len++;
    } else {
      this._buf[this._start] = sample;
      this._start = (this._start + 1) % this.capacity;
    }
  }

  clear() {
    this._buf = new Array(this.capacity);
    this._start = 0;
    this._len = 0;
  }

  /** Oldest-first copy. Debugging and tests only — allocates. @returns {T[]} */
  toArray() {
    const out = new Array(this._len);
    for (let i = 0; i < this._len; i++) out[i] = this.at(i);
    return out;
  }

  /**
   * Largest logical index whose `t` is <= the given time, or -1 if every sample
   * is later than it.
   * @param {number} t
   * @returns {number}
   */
  _floorIndex(t) {
    let lo = 0;
    let hi = this._len - 1;
    let ans = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (this.at(mid).t <= t) {
        ans = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return ans;
  }

  /**
   * What the signal was doing at time `t`.
   *
   * Binary search for the bracketing pair, then linear interpolation between
   * them. Queries outside the retained window **clamp** to the nearest end —
   * they never extrapolate. Extrapolating an aperture past the last known frame
   * invents a mouth position that was never observed, and does it most
   * aggressively at exactly the moment the camera stalled.
   *
   * The returned object is always a fresh copy stamped with the query time, so
   * callers can hold on to it without aliasing a buffered sample that is about
   * to be overwritten.
   *
   * @param {number} t
   * @returns {T|null} null only when the buffer is empty
   */
  sampleAt(t) {
    if (this._len === 0) return null;

    const first = this.at(0);
    if (t <= first.t) return { ...first };

    const last = this.at(this._len - 1);
    if (t >= last.t) return { ...last };

    const i = this._floorIndex(t);
    // t is strictly inside the window, so both neighbours exist.
    const a = this.at(i);
    const b = this.at(i + 1);
    const span = b.t - a.t;
    const alpha = span > 0 ? (t - a.t) / span : 0;
    return blend(a, b, alpha, t);
  }
}

export default RingBuffer;
